import type {
  DatabaseMetrics,
  InfraMetricsSnapshot,
  SystemMetrics,
} from '@modules/infraMetrics';

interface MetricSample {
  labels: Record<string, string>;
  value: number;
}

type MetricMap = Map<string, MetricSample[]>;

const parsePrometheus = (text: string): MetricMap => {
  const metrics: MetricMap = new Map();
  const linePattern = /^([a-zA-Z_:][a-zA-Z0-9_:]*)(?:\{(.*)\})?\s+([^\s]+)(?:\s+\d+)?$/;
  const labelPattern = /([a-zA-Z_][a-zA-Z0-9_]*)="((?:\\.|[^"])*)"/g;

  for (const rawLine of text.split('\n')) {
    const line = rawLine.trim();
    if (!line || line.startsWith('#')) continue;

    const match = line.match(linePattern);
    if (!match) continue;

    const value = Number(match[3]);
    if (!Number.isFinite(value)) continue;

    const labels: Record<string, string> = {};
    if (match[2]) {
      for (const label of Array.from(match[2].matchAll(labelPattern))) {
        labels[label[1]] = label[2]
          .replace(/\\"/g, '"')
          .replace(/\\\\/g, '\\')
          .replace(/\\n/g, '\n');
      }
    }

    const samples = metrics.get(match[1]) || [];
    samples.push({ labels, value });
    metrics.set(match[1], samples);
  }

  return metrics;
};

const firstValue = (
  metrics: MetricMap,
  name: string,
  labels?: Record<string, string>,
): number | null => {
  const sample = metrics.get(name)?.find(item =>
    !labels || Object.entries(labels).every(([key, value]) => item.labels[key] === value),
  );
  return sample?.value ?? null;
};

const sumValues = (metrics: MetricMap, name: string): number | null => {
  const samples = metrics.get(name);
  if (!samples?.length) return null;
  return samples.reduce((total, sample) => total + sample.value, 0);
};

const round = (value: number | null, digits = 2): number | null => {
  if (value === null) return null;
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
};

const percentage = (used: number | null, total: number | null): number | null => {
  if (used === null || total === null || total <= 0) return null;
  return round(Math.max(0, Math.min(100, (used / total) * 100)));
};

const buildSystemMetrics = (metrics: MetricMap): SystemMetrics => {
  const memoryTotal = firstValue(metrics, 'node_memory_MemTotal_bytes');
  const memoryAvailable = firstValue(metrics, 'node_memory_MemAvailable_bytes');
  const diskTotal =
    firstValue(metrics, 'node_filesystem_size_bytes', { mountpoint: '/data' }) ??
    firstValue(metrics, 'node_filesystem_size_bytes', { mountpoint: '/' });
  const diskAvailable =
    firstValue(metrics, 'node_filesystem_avail_bytes', { mountpoint: '/data' }) ??
    firstValue(metrics, 'node_filesystem_avail_bytes', { mountpoint: '/' });
  const diskMountpoint = firstValue(metrics, 'node_filesystem_size_bytes', { mountpoint: '/data' }) !== null
    ? '/data'
    : diskTotal !== null ? '/' : null;
  const bootTime = firstValue(metrics, 'node_boot_time_seconds');

  return {
    load1: firstValue(metrics, 'node_load1'),
    load5: firstValue(metrics, 'node_load5'),
    load15: firstValue(metrics, 'node_load15'),
    memoryTotalBytes: memoryTotal,
    memoryAvailableBytes: memoryAvailable,
    memoryUsedPercent: percentage(
      memoryTotal !== null && memoryAvailable !== null ? memoryTotal - memoryAvailable : null,
      memoryTotal,
    ),
    diskMountpoint,
    diskTotalBytes: diskTotal,
    diskAvailableBytes: diskAvailable,
    diskUsedPercent: percentage(
      diskTotal !== null && diskAvailable !== null ? diskTotal - diskAvailable : null,
      diskTotal,
    ),
    uptimeSeconds: bootTime === null
      ? null
      : Math.max(0, Math.floor(Date.now() / 1000 - bootTime)),
  };
};

const buildDatabaseMetrics = (metrics: MetricMap): DatabaseMetrics => {
  const hits = sumValues(metrics, 'pg_stat_database_blks_hit_total');
  const reads = sumValues(metrics, 'pg_stat_database_blks_read_total');
  const totalReads = hits !== null && reads !== null ? hits + reads : null;

  return {
    databaseSizeBytes:
      firstValue(metrics, 'pg_database_size_bytes', { datname: 'postgres' }) ??
      sumValues(metrics, 'pg_database_size_bytes'),
    connections: sumValues(metrics, 'pg_stat_database_num_backends'),
    poolMaxConnections:
      firstValue(metrics, 'pgbouncer_config_max_client_connections') ??
      firstValue(metrics, 'pgbouncer_config_max_client_conn'),
    poolActiveConnections: sumValues(metrics, 'pgbouncer_pools_client_active_connections'),
    poolWaitingConnections: sumValues(metrics, 'pgbouncer_pools_client_waiting_connections'),
    cacheHitPercent:
      hits !== null && totalReads !== null && totalReads > 0
        ? round((hits / totalReads) * 100, 3)
        : null,
    commitsTotal: sumValues(metrics, 'pg_stat_database_xact_commit_total'),
    rollbacksTotal: sumValues(metrics, 'pg_stat_database_xact_rollback_total'),
    deadlocksTotal: sumValues(metrics, 'pg_stat_database_deadlocks_total'),
    tempFilesTotal: sumValues(metrics, 'pg_stat_database_temp_files_total'),
    tempBytesTotal: sumValues(metrics, 'pg_stat_database_temp_bytes_total'),
  };
};

export const buildLocalInfraSnapshot = (
  supabaseText: string,
  machineText?: string | null,
): InfraMetricsSnapshot => {
  const metrics = parsePrometheus(supabaseText);
  const machineMetrics = machineText ? parsePrometheus(machineText) : null;

  return {
    generatedAt: new Date().toISOString(),
    supabase: {
      configured: true,
      available: true,
      error: null,
      metrics: {
        database: buildDatabaseMetrics(metrics),
        system: buildSystemMetrics(metrics),
      },
    },
    vm: {
      configured: true,
      available: machineMetrics !== null,
      error: null,
      metrics: machineMetrics ? buildSystemMetrics(machineMetrics) : null,
    },
  };
};
